import { useState } from 'react';
import cn from 'classnames';
// Helpers
import { withCDN } from 'helpers/cdn';
import { formatClipDuration } from 'helpers/streamHighlights';
// Ui
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Types
import { HighlightClip } from '../types';
// Styles
import styles from './HighlightCard.module.scss';

const FALLBACK_THUMBNAIL = '/images/logos/mobile-logo-black.svg';

type HighlightCardProps = {
  clip: HighlightClip;
  isPlaying?: boolean;
  onPlay: (clip: HighlightClip) => void;
  onShare?: (clip: HighlightClip) => void;
};

const HighlightCard = ({
  clip,
  isPlaying = false,
  onPlay,
  onShare,
}: HighlightCardProps): JSX.Element => {
  const [artworkFailed, setArtworkFailed] = useState(false);
  const durationLabel = formatClipDuration(clip.durationSeconds);
  const artworkSrc =
    !artworkFailed && clip.thumbnailUrl
      ? clip.thumbnailUrl
      : withCDN(FALLBACK_THUMBNAIL);

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.thumbnail}
        aria-label={`Play ${clip.title}`}
        onClick={() => onPlay(clip)}
      >
        <img
          className={styles.artwork}
          src={artworkSrc}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setArtworkFailed(true)}
        />
        <span className={styles.scrim} />

        <span className={cn(styles.playBadge, { [styles.playing]: isPlaying })}>
          {isPlaying ? (
            <MillionsIcon name="pauseSolid" size={24} />
          ) : (
            <img
              src="/images/stream-highlights/play-solid.svg"
              alt=""
              aria-hidden="true"
              className={styles.playIcon}
            />
          )}
        </span>

        {durationLabel && (
          <span className={styles.durationBadge}>{durationLabel}</span>
        )}
      </button>

      <div className={styles.details}>
        <div className={styles.detailsText}>
          <p className={styles.title} title={clip.title}>
            {clip.title}
          </p>
          {clip.sourceOffsetLabel && (
            <p className={styles.offset}>
              <img
                src="/images/stream-highlights/clock-rewind.svg"
                alt=""
                aria-hidden="true"
                className={styles.offsetIcon}
              />
              {clip.sourceOffsetLabel}
            </p>
          )}
        </div>

        {onShare && (
          <button
            type="button"
            className={styles.shareButton}
            aria-label={`Share ${clip.title}`}
            onClick={() => onShare(clip)}
          >
            <img
              src="/images/stream-highlights/share.svg"
              alt=""
              aria-hidden="true"
              className={styles.shareIcon}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default HighlightCard;
